// Pay schedules: which dates are paydays, and which days each pay covers.
// Pure; dates are 'YYYY-MM-DD' strings (see fy.js).
//
// A schedule is { frequency, anchor, days }:
//   weekly / fortnightly — every 7 / 14 days from `anchor` (a known payday);
//   monthly              — `anchor`'s day of month, every month;
//   quarterly            — `anchor`'s day of month, every 3 months from it;
//   half-monthly         — the two `days` of every month (31 = the last day).
// Days past a month's end clamp to its last day (31 → 30 Apr, 28/29 Feb).
// A pay covers the days after the previous payday up to and including its own.

import { addDays, addMonths, daysBetween, isoDate, monthLength, toYmd } from './fy.js';
import { PAY_FREQUENCIES } from './au/withholding.js';

export const DEFAULT_HALF_MONTHLY_DAYS = Object.freeze([15, 31]);

/** Normalise a stored schedule (defensive; `fallbackAnchor` when none is set). */
export function normaliseSchedule(schedule, fallbackAnchor) {
  const frequency = PAY_FREQUENCIES.includes(schedule?.frequency) ? schedule.frequency : 'monthly';
  let anchor;
  try {
    anchor = isoDate(schedule?.anchor ?? fallbackAnchor);
  } catch {
    anchor = isoDate(fallbackAnchor);
  }
  const out = { frequency, anchor };
  if (frequency === 'half-monthly') {
    const days = (Array.isArray(schedule?.days) ? schedule.days : DEFAULT_HALF_MONTHLY_DAYS)
      .map((d) => Math.min(31, Math.max(1, Math.round(Number(d)))))
      .filter(Number.isFinite);
    const unique = [...new Set(days)].sort((a, b) => a - b);
    out.days = unique.length === 2 ? unique : [...DEFAULT_HALF_MONTHLY_DAYS];
  }
  return out;
}

function monthsInRange(from, to) {
  const months = [];
  const start = toYmd(from);
  for (let date = isoDate({ ...start, d: 1 }); date <= to; date = addMonths(date, 1)) {
    months.push(toYmd(date));
  }
  return months;
}

/** All paydays from `from` to `to` (inclusive), ascending. */
export function paydaysBetween(schedule, from, to) {
  if (from > to) return [];
  const { frequency, anchor } = schedule;
  if (frequency === 'weekly' || frequency === 'fortnightly') {
    const step = frequency === 'weekly' ? 7 : 14;
    const first = Math.ceil(daysBetween(anchor, from) / step);
    const last = Math.floor(daysBetween(anchor, to) / step);
    const out = [];
    for (let k = first; k <= last; k++) out.push(addDays(anchor, k * step));
    return out;
  }
  const anchorYmd = toYmd(anchor);
  const out = [];
  for (const { y, m } of monthsInRange(from, to)) {
    let days;
    if (frequency === 'half-monthly') {
      days = schedule.days;
    } else if (frequency === 'quarterly') {
      const offset = (((y * 12 + m - (anchorYmd.y * 12 + anchorYmd.m)) % 3) + 3) % 3;
      days = offset === 0 ? [anchorYmd.d] : [];
    } else {
      days = [anchorYmd.d];
    }
    for (const d of days) {
      const date = isoDate({ y, m, d: Math.min(d, monthLength(y, m)) });
      if (date >= from && date <= to && out.at(-1) !== date) out.push(date);
    }
  }
  return out;
}

/** The latest payday strictly before `date`. */
export function previousPayday(schedule, date) {
  const window = { weekly: 7, fortnightly: 14, 'half-monthly': 31, monthly: 31, quarterly: 92 };
  return paydaysBetween(schedule, addDays(date, -window[schedule.frequency]), addDays(date, -1)).at(
    -1,
  );
}

/** The first payday on or after `date`. */
export function nextPayday(schedule, date) {
  const window = { weekly: 7, fortnightly: 14, 'half-monthly': 31, monthly: 31, quarterly: 92 };
  return paydaysBetween(schedule, date, addDays(date, window[schedule.frequency]))[0];
}

/** The days a pay covers: { start, end } — after the previous payday, up to this one. */
export function payPeriod(schedule, payday) {
  return { start: addDays(previousPayday(schedule, payday), 1), end: payday };
}
